/** @jsxImportSource @emotion/react */
import { css, jsx } from '@emotion/react';
import { Link, LinkProps } from 'react-router-dom';

import DropboxIcon from '@atlaskit/icon/glyph/dropbox';
import FilterIcon from '@atlaskit/icon/glyph/filter';
import WorkIcon from '@atlaskit/icon/glyph/folder';
import LightbulbIcon from '@atlaskit/icon/glyph/lightbulb';
import CustomerIcon from '@atlaskit/icon/glyph/person';
import QueueIcon from '@atlaskit/icon/glyph/queues';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import LanguageIcon from '@atlaskit/icon/glyph/world';
import CreditcardIcon from '@atlaskit/icon/glyph/creditcard';
import BillingIcon from '@atlaskit/icon/glyph/billing';
import TrayIcon from '@atlaskit/icon/glyph/tray';
import GraphBarIcon from '@atlaskit/icon/glyph/graph-bar';
import SuitcaseIcon from '@atlaskit/icon/glyph/suitcase';
import PeopleIcon from '@atlaskit/icon/glyph/people';
import TableIcon from '@atlaskit/icon/glyph/table';
import OfficeBuildingIcon from '@atlaskit/icon/glyph/office-building';
import MediaServicesAddCommentIcon from '@atlaskit/icon/glyph/media-services/add-comment';
import DashboardIcon from '@atlaskit/icon/glyph/dashboard';
import Toggle from '@atlaskit/toggle';
import ScheduleIcon from '@atlaskit/icon/glyph/schedule';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
// import RetryIcon from '@atlaskit/icon/glyph/retry';

import {
  ButtonItem,
  CustomItem,
  LinkItem,
  NavigationFooter,
  NavigationHeader,
  NestableNavigationContent,
  NestingItem,
  Section,
  SideNavigation,
  Header,
  CustomItemComponentProps, Footer,
} from '@atlaskit/side-navigation';
import React, { forwardRef, useState } from 'react';
import { CompanyAvatar } from 'components/avatar';
import Popup from '@atlaskit/popup';
import {
  MenuGroup,
  PopupMenuGroup,
  ButtonItem as MenuButtonItem,
  CustomItem as CustomMenuItem,
  LinkItem as MenuLinkItem,
} from '@atlaskit/menu';
import { useLocation } from 'react-router';
import { useCurrentCompany } from '../../auth/CurrentCompany';
import { useAuth } from '../../auth';
import { Checkbox } from '@atlaskit/checkbox';

// import AppFrame from './common/app-frame';
// import SampleFooter from './common/sample-footer';
// import SampleHeader from './common/sample-header';

type CustomProps = CustomItemComponentProps & LinkProps;

const CustomLink = forwardRef<HTMLAnchorElement, CustomProps>(
  (props: CustomProps, ref) => {
    const { children, ...rest } = props;
    return (
      <Link ref={ref} {...rest}>
        {children}
      </Link>
    );
  }
);

const InteractiveContainer = ({
  children,
  ...props
}: CustomItemComponentProps) => {
  return <a {...props}>{children}</a>;
};

const MyNavigationHeader: React.FC = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentCompany = useCurrentCompany();
  console.log('currentCompany header', currentCompany);
  const { currentUser, logout } = useAuth();
  const {pathname} = useLocation();
  const getNewCompanyPath = (slug: string) => pathname.replace(/^\/.*?\//, `/${slug}/`);
  const oldVersionHref = pathname.replace(/\/r.*/,'/dashboard')
  if (!currentCompany) return null;

  const companies = [
    { name: 'Thymo Inc.', website: 'administratiebox.nl', slug: 'thymo-inc' },
    {
      name: 'AdministratieBox B.V.',
      website: 'administratiebox.nl',
      slug: 'administratiebox-bv',
    },
    {
      name: 'iTupt B.V.',
      website: 'itupt.com',
      slug: 'itupt-bv',
    },
    {
      name: 'AATD Beheer B.V.',
      website: 'aatd.nl',
      slug: 'aatd-beheer-bv',
    },
  ];

  return (
    <NavigationHeader>
      <Popup
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        placement="bottom"
        offset={140}
        content={() => (
          <MenuGroup minWidth={320}>
            <Section title="Companies">
              {companies.map((company) => (
                <CustomMenuItem
                  key={company.slug}
                  component={CustomLink}
                  to={getNewCompanyPath(company.slug)}
                  iconBefore={<CompanyAvatar website={company.website} />}
                  description={currentUser?.email}
                  onClick={()=> setIsOpen(false)}
                >
                  {company.name}
                </CustomMenuItem>
              ))}
            </Section>
            <Section title="Actions">
              <MenuLinkItem href={oldVersionHref}>Old version</MenuLinkItem>
              <MenuLinkItem href="/boekhouder/bedrijven">Bedrijven overzicht</MenuLinkItem>
              <MenuButtonItem onClick={() => logout()}>Logout</MenuButtonItem>
            </Section>
          </MenuGroup>
        )}
        trigger={(triggerProps) => (
          // @ts-ignore
          <div {...triggerProps}>
            <Header
              onClick={() => {
                setIsOpen(true);
              }}
              component={InteractiveContainer}
              iconBefore={
                <CompanyAvatar
                  website={currentCompany?.website || 'administratiebox.nl'}
                />
              }
              description={currentUser?.name}
            >
              {currentCompany.name}
            </Header>
          </div>
        )}
      />
    </NavigationHeader>
  );
};

const LanguageSettings = () => {
  return (
    <NestingItem
      iconBefore={<LanguageIcon label="" />}
      id="3-1"
      title="Language settings"
    >
      <Section>
        <ButtonItem>Customize</ButtonItem>

        <NestingItem id="3-1-1" title="German Settings">
          <Section>
            <ButtonItem>Hallo Welt!</ButtonItem>
          </Section>
        </NestingItem>
        <NestingItem id="3-1-2" title="English Settings">
          <Section>
            <ButtonItem>Hello World!</ButtonItem>
          </Section>
        </NestingItem>
      </Section>
    </NestingItem>
  );
};
// {/*<SampleHeader />*/}

const CustomSideNavigation = () => {

  return (
    <SideNavigation label="project" testId="side-navigation">
      <MyNavigationHeader />

      <NestableNavigationContent initialStack={[]}>
        <Section>
          <CustomItem
            iconBefore={<DashboardIcon label="" />}
            to="dashboard"
            component={CustomLink}
          >
            Dashboard
          </CustomItem>
          <CustomItem
            iconBefore={<WorkIcon label="" />}
            to="inbox"
            component={CustomLink}
          >
            Inbox
          </CustomItem>
          <CustomItem
            iconBefore={<CreditcardIcon label="" />}
            to="bank-accounts"
            component={CustomLink}
          >
            Bank
          </CustomItem>
          <CustomItem
            iconBefore={<SuitcaseIcon label="" />}
            to="purchase"
            component={CustomLink}
          >
            Purchases
          </CustomItem>
          {/* <Section title="Sales"> */}
          {/*  <CustomItem iconBefore={<CustomerIcon label="" />} to="sale/customers" component={CustomLink}> */}
          {/*    Customers */}
          {/*  </CustomItem> */}
          {/*  <CustomItem  iconBefore={<BillingIcon label="" />} to="sale" component={CustomLink}> */}
          {/*    Sales */}
          {/*  </CustomItem> */}
          {/* </Section> */}
          <NestingItem
            // isSelected
            id="1"
            title="Sales"
            iconBefore={<BillingIcon label="" />}
          >
            <Section title="Sales">
              <CustomItem
                iconBefore={<BillingIcon label="" />}
                to="sale/invoices"
                component={CustomLink}
              >
                Invoices
              </CustomItem>
              <CustomItem
                iconBefore={<ScheduleIcon label="" />}
                to="sale/recurring-invoices"
                component={CustomLink}
              >
                Recurring invoices
              </CustomItem>
              <CustomItem
                iconBefore={<CustomerIcon label="" />}
                to="sale/customers"
                component={CustomLink}
              >
                Customers
              </CustomItem>
              <CustomItem
                iconBefore={<TableIcon label="" />}
                to="sale/items"
                component={CustomLink}
              >
                Products & Services
              </CustomItem>
              <CustomItem
                iconBefore={<SettingsIcon label="" />}
                to="sale/invoice-settings"
                component={CustomLink}
              >
                Invoice settings
              </CustomItem>
            </Section>
          </NestingItem>
          {/* <NestingItem */}
          {/*  id="2" */}
          {/*  testId="filter-nesting-item" */}
          {/*  title="Filters" */}
          {/*  iconBefore={<FilterIcon label="" />} */}
          {/*  iconAfter={<LightbulbIcon label="" />} */}
          {/* > */}
          {/*  <Section> */}
          {/*    <ButtonItem>Search issues</ButtonItem> */}
          {/*  </Section> */}
          {/*  <Section title="Starred"> */}
          {/*    <ButtonItem>Everything me</ButtonItem> */}
          {/*    <ButtonItem>My open issues</ButtonItem> */}
          {/*    <ButtonItem>Reported by me</ButtonItem> */}
          {/*  </Section> */}
          {/*  <Section hasSeparator title="Other"> */}
          {/*    <ButtonItem>All issues</ButtonItem> */}
          {/*    <ButtonItem>Open issues</ButtonItem> */}
          {/*    <ButtonItem>Created recently</ButtonItem> */}
          {/*    <ButtonItem>Resolved recently</ButtonItem> */}
          {/*  </Section> */}
          {/*  <Section hasSeparator> */}
          {/*    <ButtonItem>View all filters</ButtonItem> */}
          {/*  </Section> */}
          {/* </NestingItem> */}
          {/* <Section title="Settings"> */}

          <NestingItem
            id="3"
            iconBefore={<SettingsIcon label="" />}
            title="Settings"
            // isSelected
          >
            <Section>
              <LanguageSettings />
            </Section>
          </NestingItem>
          {/* </Section> */}
          {/* <NestingItem */}
          {/*  id="4" */}
          {/*  iconBefore={<DropboxIcon label="" />} */}
          {/*  title="Dropbox" */}
          {/*  isDisabled */}
          {/* > */}
          {/*  <span /> */}
          {/* </NestingItem> */}
        </Section>
      </NestableNavigationContent>
       <NavigationFooter>
         <Header iconBefore={<CompanyAvatar website={'administratiebox.nl'}/>}>AdministratieBox</Header>
       </NavigationFooter>
    </SideNavigation>
  );
};
//         <SampleFooter />

export default CustomSideNavigation;
